"use client";

import { useMemo, useState } from "react";
import { DocumentPreview } from "@/components/tool-kit/document-preview";
import {
  Checkbox,
  Panel,
  SelectField,
  TextAreaField,
  TextField,
} from "@/components/tool-kit/primitives";
import { BUTTON_SECONDARY } from "@/components/tool-kit/styles";
import { useToolUi } from "@/components/tools/tool-ui";
import {
  ARC_TYPES,
  type ArcType,
  buildCharacterProfile,
  CHARACTER_ROLES,
  type CharacterProfileInput,
  type CharacterRole,
} from "@/lib/tool-kit/essay-templates/character-profile";

/** English strings. A translated content file overrides these through `ui`. */
export const CHARACTER_PROFILE_GENERATOR_STRINGS = {
  tryExample: "Try an example",
  clear: "Clear the form",
  basicsHeading: "Basics",
  nameLabel: "Name",
  namePlaceholder: "e.g. Marcus Hale",
  ageLabel: "Age",
  agePlaceholder: "e.g. 34",
  roleLabel: "Role in the story",
  roleNone: "Not set",
  occupationLabel: "Occupation",
  occupationPlaceholder: "e.g. Night-shift paramedic",
  homeLabel: "Where they live",
  homePlaceholder: "e.g. A rented room above a laundromat",
  surfaceHeading: "Appearance, personality, and past",
  appearanceLabel: "Appearance",
  appearancePlaceholder:
    "What a stranger would notice first — and one telling detail.",
  personalityLabel: "Personality — key traits, and one contradiction",
  personalityPlaceholder: "e.g. Patient with strangers, impatient with family.",
  backstoryLabel: "Backstory",
  backstoryPlaceholder: "What happened before page one that still shapes them?",
  driveHeading: "What drives them",
  goalLabel: "Goal — what they want",
  goalPlaceholder: "e.g. To earn enough to bring his sister home",
  motivationLabel: "Motivation — why they want it",
  motivationPlaceholder: "e.g. He promised their mother he would",
  fearLabel: "Fear — what they most want to avoid",
  fearPlaceholder: "e.g. Becoming as careless as his father",
  flawLabel: "Flaw — the belief or habit that holds them back",
  flawPlaceholder: "e.g. He thinks asking for help is a kind of failure.",
  arcHeading: "Arc and relationships",
  arcLabel: "Arc type",
  arcNone: "Not decided",
  arcNames: {
    positive: "Positive change",
    negative: "Negative change",
    flat: "Flat (steadfast)",
  },
  arcNotesLabel: "How they change",
  arcNotesPlaceholder:
    "Who they are at the start, the turning point, and who they are at the end.",
  relationshipsLabel: "Relationships — one per line, “Name — relationship”",
  relationshipsPlaceholder:
    "e.g. Dana Hale — younger sister he is trying to protect",
  voiceLabel: "Voice — how they speak",
  voicePlaceholder:
    "Vocabulary, rhythm, what they avoid saying, a phrase they overuse.",
  questionsLabel: "Add interview questions to deepen the profile",
  fileName: "character-profile",
  document: {
    heading: "Your character profile",
    prompt: "To write",
    guidance: "Note",
    copy: "Copy",
    copied: "Copied!",
    downloadText: "Download .txt",
    downloadMarkdown: "Download .md",
    print: "Print",
    remaining: "one: part still to write|other: parts still to write",
    complete: "Every part of the profile has your content.",
  },
  defaultTitle: "Character Profile",
  titleWithName: "Character Profile: {name}",
  headings: {
    glance: "At a Glance",
    appearance: "Appearance",
    personality: "Personality",
    backstory: "Backstory",
    drive: "Goal, Motivation, and Fear",
    flaw: "Flaw",
    arc: "Arc",
    relationships: "Relationships",
    voice: "Voice",
    questions: "Questions to Answer",
  },
  fields: {
    name: "Name",
    age: "Age",
    role: "Role",
    occupation: "Occupation",
    home: "Lives in",
    goal: "Goal",
    motivation: "Motivation",
    fear: "Fear",
  },
  roles: {
    protagonist: "Protagonist",
    antagonist: "Antagonist",
    supporting: "Supporting character",
    mentor: "Mentor",
    "love-interest": "Love interest",
    foil: "Foil",
    narrator: "Narrator",
  },
  arcs: {
    positive:
      "A positive change arc: the character overcomes the flaw or a false belief, and ends better able to get what they truly need.",
    negative:
      "A negative change arc: the character clings to the flaw, or trades one false belief for a worse one, and ends diminished or destroyed.",
    flat: "A flat arc: the character already holds the truth the story tests; instead of changing, they change the people and the world around them.",
  },
  prompts: {
    appearance:
      "Describe what a stranger would notice first, plus one detail that reveals character — bitten nails, an expensive watch they can't afford.",
    personality:
      "List three or four core traits, then one contradiction: a trait that seems to clash with the rest.",
    backstory:
      "What happened before page one that still shapes them? Focus on the wound or turning point behind their fear.",
    goal: "Goal — what does this character want, concretely, during the story?",
    motivation:
      "Motivation — why do they want it? Tie the reason to their backstory.",
    fear: "Fear — what do they most want to avoid, and how does the goal force them toward it?",
    flaw: "Name the belief or habit that stops them from getting what they need, and how the plot will test it.",
    arc: "Describe who they are at the start, the turning point that challenges them, and who they are at the end.",
    relationships:
      "List the people who matter most to them, one per line — “Name — relationship” — and note one tension in each.",
    voice:
      "How do they talk? Note their vocabulary, sentence length, what they avoid saying, and a phrase they overuse. Write one line only they would say.",
  },
  questionsNote:
    "Answer in the character's own voice. The answers you didn't expect are usually the useful ones.",
  questions: [
    "What do they do in the first ten minutes after they wake up?",
    "What would they grab first if their home were on fire?",
    "What lie do they tell most often — to other people, or to themselves?",
    "Who was the last person they disappointed, and how did they handle it?",
    "What do they spend money on that they would be embarrassed to admit?",
    "What makes them laugh when they shouldn't?",
    "What is the one thing they would never forgive?",
    "What do other people consistently get wrong about them?",
    "Where do they go when they want to be alone?",
    "What would they have to lose before they would finally change?",
  ],
  example: {
    name: "Ines Okafor",
    age: "24",
    occupation: "Junior surveyor at the city records office",
    home: "A rented attic above a boatyard on the edge of the flooded quarter",
    appearance:
      "Tall and wiry, with ink-stained fingers and a waterproof field jacket she never takes off. She keeps her hair cropped short so it can't get in the way of her instruments. Her grandmother's silver compass hangs from a cord around her neck.",
    personality:
      "Precise, stubborn, dryly funny, and loyal to facts over people. She is shy in groups but blunt one-on-one, and she cannot let an inconsistency go.",
    backstory:
      "Ines grew up in the quarter before the floods emptied it. Her grandmother, a ferry pilot, refused to leave and was moved out by the city when Ines was fifteen; she died within the year. Ines blames the official evacuation maps, which marked their street as empty months before it was.",
    goal: "To prove the flooded quarter is still lived in before the council sells it.",
    motivation:
      "If the records are false, her grandmother's eviction was built on a lie — and she can finally say so in public.",
    fear: "That the maps she has trusted all her life can be bent by whoever holds the pen.",
    flaw: "She believes evidence speaks for itself, so she won't ask for help or try to persuade anyone.",
    arcNotes:
      "She starts as a lone surveyor who thinks the data will win the argument. At the midpoint, her flawless survey is dismissed in a single meeting. By the end she has learned to gather witnesses — the quarter's remaining residents — and to speak for them herself.",
    relationships:
      "Arlo Venn — the deputy mayor, once her mentor at the records office, now her antagonist\nTomas Reyes — a boat mechanic in the quarter who becomes her closest ally\nAgnes Okafor — her late grandmother, whose compass and stories still guide her",
    voice:
      "Short, exact sentences; she corrects other people's numbers mid-conversation and changes the subject to measurements when feelings come up. A typical line: “That's not a guess. I measured it twice.”",
  },
} as const;

/** The example's choices that are settings, not prose — kept out of the translatable strings. */
const EXAMPLE_SETTINGS = {
  role: "protagonist",
  arc: "positive",
  questions: true,
} as const;

const EMPTY: CharacterProfileInput = {
  name: "",
  age: "",
  role: "",
  occupation: "",
  home: "",
  appearance: "",
  personality: "",
  backstory: "",
  goal: "",
  motivation: "",
  fear: "",
  flaw: "",
  arc: "",
  arcNotes: "",
  relationships: "",
  voice: "",
  questions: false,
};

type TextKey = Exclude<
  keyof CharacterProfileInput,
  "role" | "arc" | "questions"
>;

type Strings = typeof CHARACTER_PROFILE_GENERATOR_STRINGS;

/** The keys of the strings object whose values are plain strings — labels, placeholders. */
type StringKey = {
  [K in keyof Strings]: Strings[K] extends string ? K : never;
}[keyof Strings];

interface AreaField {
  key: TextKey;
  label: StringKey;
  placeholder: StringKey;
}

const SURFACE_FIELDS = [
  {
    key: "appearance",
    label: "appearanceLabel",
    placeholder: "appearancePlaceholder",
  },
  {
    key: "personality",
    label: "personalityLabel",
    placeholder: "personalityPlaceholder",
  },
  {
    key: "backstory",
    label: "backstoryLabel",
    placeholder: "backstoryPlaceholder",
  },
] as const;

const DRIVE_FIELDS = [
  { key: "goal", label: "goalLabel", placeholder: "goalPlaceholder" },
  {
    key: "motivation",
    label: "motivationLabel",
    placeholder: "motivationPlaceholder",
  },
  { key: "fear", label: "fearLabel", placeholder: "fearPlaceholder" },
  { key: "flaw", label: "flawLabel", placeholder: "flawPlaceholder" },
] as const;

export default function CharacterProfileGeneratorWidget() {
  const strings = useToolUi(CHARACTER_PROFILE_GENERATOR_STRINGS);
  const [input, setInput] = useState<CharacterProfileInput>(EMPTY);
  const doc = useMemo(
    () => buildCharacterProfile(input, strings),
    [input, strings]
  );
  const update = (key: TextKey) => (value: string) =>
    setInput((previous) => ({ ...previous, [key]: value }));
  const area = (field: AreaField, rows = 3) => (
    <TextAreaField
      id={`cp-${field.key}`}
      key={field.key}
      label={strings[field.label]}
      onChange={update(field.key)}
      placeholder={strings[field.placeholder]}
      prose
      rows={rows}
      value={input[field.key]}
    />
  );

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap gap-3">
        <button
          className={BUTTON_SECONDARY}
          onClick={() =>
            setInput({ ...EMPTY, ...strings.example, ...EXAMPLE_SETTINGS })
          }
          type="button"
        >
          {strings.tryExample}
        </button>
        <button
          className={BUTTON_SECONDARY}
          onClick={() => setInput(EMPTY)}
          type="button"
        >
          {strings.clear}
        </button>
      </div>

      <Panel title={strings.basicsHeading}>
        <div className="grid gap-4 sm:grid-cols-2">
          <TextField
            id="cp-name"
            label={strings.nameLabel}
            onChange={update("name")}
            placeholder={strings.namePlaceholder}
            value={input.name}
          />
          <TextField
            id="cp-age"
            label={strings.ageLabel}
            onChange={update("age")}
            placeholder={strings.agePlaceholder}
            value={input.age}
          />
          <SelectField<CharacterRole>
            id="cp-role"
            label={strings.roleLabel}
            onChange={(role) => setInput((previous) => ({ ...previous, role }))}
            options={[
              { value: "", label: strings.roleNone },
              ...CHARACTER_ROLES.map((value) => ({
                value,
                label: strings.roles[value],
              })),
            ]}
            value={input.role}
          />
          <TextField
            id="cp-occupation"
            label={strings.occupationLabel}
            onChange={update("occupation")}
            placeholder={strings.occupationPlaceholder}
            value={input.occupation}
          />
        </div>
        <TextField
          id="cp-home"
          label={strings.homeLabel}
          onChange={update("home")}
          placeholder={strings.homePlaceholder}
          value={input.home}
        />
      </Panel>

      <Panel title={strings.surfaceHeading}>
        {SURFACE_FIELDS.map((field) => area(field))}
      </Panel>

      <Panel title={strings.driveHeading}>
        {DRIVE_FIELDS.map((field) => area(field, 2))}
      </Panel>

      <Panel title={strings.arcHeading}>
        <div className="max-w-xs">
          <SelectField<ArcType>
            id="cp-arc"
            label={strings.arcLabel}
            onChange={(arc) => setInput((previous) => ({ ...previous, arc }))}
            options={[
              { value: "", label: strings.arcNone },
              ...ARC_TYPES.map((value) => ({
                value,
                label: strings.arcNames[value],
              })),
            ]}
            value={input.arc}
          />
        </div>
        {area({
          key: "arcNotes",
          label: "arcNotesLabel",
          placeholder: "arcNotesPlaceholder",
        })}
        {area({
          key: "relationships",
          label: "relationshipsLabel",
          placeholder: "relationshipsPlaceholder",
        })}
        {area({
          key: "voice",
          label: "voiceLabel",
          placeholder: "voicePlaceholder",
        })}
        <Checkbox
          checked={input.questions}
          label={strings.questionsLabel}
          onChange={(questions) =>
            setInput((previous) => ({ ...previous, questions }))
          }
        />
      </Panel>

      <DocumentPreview
        doc={doc}
        fileName={strings.fileName}
        idPrefix="cp"
        labels={strings.document}
      />
    </div>
  );
}
